// WebSocket client for live gameplay. Players connect with ?role=player; admins
// connect with ?role=admin&token=<idToken>&gameId=<id>. Reconnects with backoff
// and re-syncs on reconnect.

import { loadConfig } from '../config';
import { Action, ServerMessage } from '../shared/protocol';

export type Role = 'player' | 'admin';
type Listener = (msg: ServerMessage) => void;
type StatusListener = (open: boolean) => void;

export interface ConnectOptions {
  role: Role;
  token?: string;
  gameId?: string;
}

export class GameSocket {
  private ws: WebSocket | null = null;
  private opts: ConnectOptions;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<StatusListener>();
  private closedByUser = false;
  private backoff = 500;

  constructor(opts: ConnectOptions) {
    this.opts = opts;
  }

  async connect(): Promise<void> {
    const cfg = await loadConfig();
    const params = new URLSearchParams({ role: this.opts.role });
    if (this.opts.token) params.set('token', this.opts.token);
    if (this.opts.gameId) params.set('gameId', this.opts.gameId);
    const url = `${cfg.wsApiUrl}?${params.toString()}`;

    this.ws = new WebSocket(url);
    this.ws.onopen = () => {
      this.backoff = 500;
      this.statusListeners.forEach((l) => l(true));
      // Ask for a fresh snapshot after (re)connecting.
      this.send(Action.Sync);
    };
    this.ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as ServerMessage;
        this.listeners.forEach((l) => l(msg));
      } catch {
        /* ignore malformed */
      }
    };
    this.ws.onclose = () => {
      this.statusListeners.forEach((l) => l(false));
      if (!this.closedByUser) this.scheduleReconnect();
    };
    this.ws.onerror = () => this.ws?.close();
  }

  private scheduleReconnect() {
    const delay = Math.min(this.backoff, 8000);
    this.backoff *= 2;
    setTimeout(() => {
      if (!this.closedByUser) void this.connect();
    }, delay);
  }

  send(action: string, data?: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ action, data }));
    }
  }

  onMessage(l: Listener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  onStatus(l: StatusListener): () => void {
    this.statusListeners.add(l);
    return () => this.statusListeners.delete(l);
  }

  close() {
    this.closedByUser = true;
    this.ws?.close();
  }
}
